import {
	Button,
	CircularProgress,
	Grid2,
	Snackbar,
	SnackbarCloseReason,
	TextField,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import MesfModal from "../../../../../components/modals/modal.mesf";
import { Alert } from "../../../../../controls/alert";
import { HelmetDexteel } from "../../../../../helmet/HelmetDexteel";
import { Profile } from "../../models/Profile";
import { cloneProfile } from "../../repositories/ProfilesRepository";

type Props = {
	profile: Profile | null;
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	suffixTitle?: string;
};

type CloneFormValues = {
	NewProfileName: string;
};

export const CloneProfile = ({ profile, show, onHide, suffixTitle }: Props) => {
	const [openSnackbar, setOpenSnackbar] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState("");
	const queryClient = useQueryClient();

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<CloneFormValues>();

	const cloneProfileMutation = useMutation({
		mutationFn: cloneProfile,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["profiles"] });
			setOpenSnackbar(true);
			onHide(false);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => {
			setIsSubmitLoading(false);
		},
	});

	const onSubmit: SubmitHandler<CloneFormValues> = async (data) => {
		if (!profile?.ProfileId) return;
		setIsSubmitLoading(true);
		await cloneProfileMutation.mutate({
			SourceProfileId: profile.ProfileId,
			NewProfileName: data.NewProfileName,
		});
	};

	const handleClose = (
		event: React.SyntheticEvent<any> | Event,
		reason?: SnackbarCloseReason,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setOpenSnackbar(false);
	};

	useEffect(() => {
		if (show && profile) {
			reset({ NewProfileName: `${profile.ProfileName} Copy` });
		}
	}, [show, profile]);

	return (
		<>
			{show && (
				<HelmetDexteel
					title={`Clone Profile${suffixTitle ? ` - ${suffixTitle}` : ""}`}
				/>
			)}
			<Grid2 container>
				<Grid2>
					<MesfModal
						title="CLONE PROFILE"
						open={show}
						handleClose={() => onHide(false)}
						id="clone-profile-modal"
						maxWidth="md"
					>
						<form onSubmit={handleSubmit(onSubmit)}>
							<MesfModal.Content style={{ padding: "15px 30px" }}>
								<Grid2 container spacing={1}>
									<Grid2 size={{ md: 12, xs: 12 }}>
										<TextField
											{...register("NewProfileName", {
												required: true,
												validate: {
													notWhitespace: (value) =>
														value.trim() !== "" ||
														"Profile name cannot be only whitespace",
												},
											})}
											label="New profile name"
											variant="outlined"
											error={!!errors.NewProfileName}
											fullWidth
											margin="dense"
											autoComplete="off"
										/>{" "}
										{errors.NewProfileName?.type === "required" && (
											<span style={{ fontSize: 12, color: "#F44336" }}>
												Profile name is required
											</span>
										)}
										{errors.NewProfileName?.type === "notWhitespace" && (
											<span style={{ fontSize: 12, color: "#F44336" }}>
												{errors.NewProfileName.message}
											</span>
										)}
									</Grid2>
								</Grid2>
							</MesfModal.Content>
							<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
								<Grid2 container spacing={2} justifyContent="flex-end">
									<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
										<Button
											fullWidth
											variant="contained"
											color="inherit"
											onClick={() => onHide(false)}
										>
											Cancel
										</Button>
									</Grid2>
									<Grid2 size={{ md: 3, xs: 12 }} style={{ margin: 0 }}>
										<Button
											fullWidth
											startIcon={
												isSubmitLoading && <CircularProgress size="1rem" />
											}
											disabled={isSubmitLoading}
											variant="contained"
											color="primary"
											type="submit"
										>
											Clone
										</Button>
									</Grid2>
								</Grid2>
							</MesfModal.Actions>
						</form>
					</MesfModal>
				</Grid2>
			</Grid2>
			<Snackbar
				open={openSnackbar}
				autoHideDuration={2500}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="success" onClose={handleClose}>
					The profile was cloned successfully
				</Alert>
			</Snackbar>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error Cloning Profile"
			/>
		</>
	);
};
